import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { IconEye, IconEyeOff } from "@tabler/icons-react";

import AuthLayout, { authField, AuthLabel } from "../../components/auth/AuthLayout";
import { loginAPI } from "../../../services/authService";
import { setAccount, showAppAlert } from "../../../store/reducers/appreducer";
import { paths } from "../../../routes/paths";
import { setUiPreference, UI_V1 } from "../../../utils/uiPreference";
import tokens from "../../theme/tokens";

const { color, font } = tokens;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SignIn = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [data, setData] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "", form: "" }));
  };

  const validate = () => {
    const next = {};
    if (!EMAIL.test(data.email)) next.email = "Enter a valid email address.";
    if (!data.password) next.password = "Password is required.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const response = await loginAPI(data.email, data.password);

      if (response.error) {
        setErrors({ form: response.errorMessage });
        dispatch(
          showAppAlert({
            show: true,
            alertType: "error",
            alertMessage: response.errorMessage,
          })
        );
        return;
      }

      dispatch(
        setAccount({
          data: response,
          userId: response.userId,
          company_id: response.company_id,
        })
      );
      // Signing in here IS a choice of UI — remember it, so the old login stops asking and
      // future logins land here too.
      setUiPreference(UI_V1);

      // Both UIs share the same token, so a session started here works in the old UI too.
      navigate(paths.v1.jobs);
    } catch (err) {
      console.error("Error during login:", err);
      setErrors({ form: "An error occurred. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Welcome back to your hiring console."
      footer={
        <Box sx={{ display: "none" }}>
          New here?{" "}
          <Box
            component={Link}
            to={paths.v1.signup}
            sx={{
              font: `700 12.5px ${font.sans}`,
              color: color.indigo,
              textDecoration: "none",
            }}
          >
            Create an account
          </Box>
        </Box>
      }
    >
      <form onSubmit={handleSubmit} noValidate>
        <AuthLabel>Work email</AuthLabel>
        <TextField
          fullWidth
          name="email"
          type="email"
          placeholder="you@company.com"
          value={data.email}
          onChange={handleChange}
          error={Boolean(errors.email)}
          helperText={errors.email}
          sx={{ ...authField, mb: 1.75 }}
        />

        <AuthLabel>Password</AuthLabel>
        <TextField
          fullWidth
          name="password"
          type={showPassword ? "text" : "password"}
          placeholder="••••••••"
          value={data.password}
          onChange={handleChange}
          error={Boolean(errors.password)}
          helperText={errors.password}
          sx={{ ...authField, mb: 1 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  edge="end"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <IconEyeOff size={16} stroke={1.75} color={color.textMuted} />
                  ) : (
                    <IconEye size={16} stroke={1.75} color={color.textMuted} />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ textAlign: "right", mb: 2.25 }}>
          <Box
            component={Link}
            to={paths.v1.forgotPassword}
            sx={{
              font: `500 12px ${font.sans}`,
              color: color.indigo,
              textDecoration: "none",
            }}
          >
            Forgot password?
          </Box>
        </Box>

        {errors.form && (
          <Box sx={{ mb: 1.5, font: `400 11.5px ${font.sans}`, color: color.danger }}>
            {errors.form}
          </Box>
        )}

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading}
          sx={{ py: 1.5, fontSize: 13.5 }}
        >
          {loading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Continue →"}
        </Button>
      </form>
    </AuthLayout>
  );
};

export default SignIn;
